/** Sherrys Wardrobe editorial trio. Native touch scrolling + responsive snap, no dependencies. */
(() => {
  if (customElements.get('sherrys-editorial-trio')) return;

  class SherrysEditorialTrio extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-swet-track]');
      this.cards = Array.from(this.querySelectorAll('[data-swet-card]'));
      this.dots = Array.from(this.querySelectorAll('[data-swet-to]'));
      if (!this.track || !this.cards.length) return;

      this.initialMobileIndex = Math.max(0, Math.min(this.cards.length - 1, Number(this.dataset.mobileStart) || 0));
      this.wasMobile = window.matchMedia('(max-width: 749px)').matches;
      this.initialized = false;
      this.activeIndex = 0;
      this.scrollFrame = 0;

      const prev = this.querySelector('[data-swet-prev]');
      const next = this.querySelector('[data-swet-next]');
      prev?.addEventListener('click', () => this.move(-1), { signal });
      next?.addEventListener('click', () => this.move(1), { signal });
      this.dots.forEach(dot => dot.addEventListener('click', () => this.goto(Number(dot.dataset.swetTo)), { signal }));
      this.track.addEventListener('scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.update();
        });
      }, { passive: true, signal });
      // Theme editor: selecting a card block navigates to that card.
      this.addEventListener('shopify:block:select', event => {
        const card = event.target.closest('[data-swet-card]');
        const index = this.cards.indexOf(card);
        if (index !== -1) this.goto(index);
      }, { signal });
      this.track.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        // Only override keyboard when the scroll region itself owns focus.
        if (event.target !== this.track) return;
        event.preventDefault();
        this.move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });

      const resize = () => {
        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        if (!this.initialized) {
          this.initialized = true;
          if (isMobile) this.goto(this.initialMobileIndex, 'instant');
        } else if (isMobile !== this.wasMobile) {
          this.goto(isMobile ? this.initialMobileIndex : 0, 'instant');
        }
        this.wasMobile = isMobile;
        this.update();
      };
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(resize);
        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener('resize', resize, { passive: true, signal });
      }
      this.connectFrame = requestAnimationFrame(resize);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      if (this.connectFrame) cancelAnimationFrame(this.connectFrame);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    goto(index, behavior = 'smooth') {
      if (!this.track || !this.cards?.length) return;
      const card = this.cards[Math.max(0, Math.min(this.cards.length - 1, index))];
      const trackBox = this.track.getBoundingClientRect();
      const cardBox = card.getBoundingClientRect();
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const delta = isMobile
        ? (cardBox.left + cardBox.width / 2) - (trackBox.left + trackBox.width / 2)
        : (cardBox.left - trackBox.left);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduceMotion || behavior === 'instant') {
        // Avoid browser-specific 'instant' behavior values and CSS smooth-scrolling during initial alignment.
        const oldBehavior = this.track.style.scrollBehavior;
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft += delta;
        this.track.style.scrollBehavior = oldBehavior;
      } else {
        this.track.scrollTo({ left: this.track.scrollLeft + delta, behavior: 'smooth' });
      }
      this.update();
    }

    move(step) {
      this.goto(this.getClosestIndex() + step);
    }

    getClosestIndex() {
      if (!this.cards?.length || !this.track) return 0;
      const trackBox = this.track.getBoundingClientRect();
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const target = isMobile ? trackBox.left + trackBox.width / 2 : trackBox.left;
      let closest = 0;
      let distance = Infinity;
      this.cards.forEach((card, index) => {
        const box = card.getBoundingClientRect();
        const location = isMobile ? box.left + box.width / 2 : box.left;
        const diff = Math.abs(location - target);
        if (diff < distance) { distance = diff; closest = index; }
      });
      return closest;
    }

    update() {
      if (!this.track) return;
      const idx = this.getClosestIndex();
      this.activeIndex = idx;
      this.dots.forEach((dot, index) => dot.setAttribute('aria-current', String(index === idx)));
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const left = Math.max(0, this.track.scrollLeft);
      this.dataset.scrollable = String(maxScroll > 2);
      const prev = this.querySelector('[data-swet-prev]');
      const next = this.querySelector('[data-swet-next]');
      if (prev) prev.disabled = left <= 2;
      if (next) next.disabled = left >= maxScroll - 2;
    }
  }

  customElements.define('sherrys-editorial-trio', SherrysEditorialTrio);
})();
